import { deleteField, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import {
  getMessaging,
  isSupported,
  onMessage,
  onRegistered,
  register,
  unregister,
} from "firebase/messaging";
import { auth, db } from "../../firebase";

// Firebase recommends FID-based registration. The Functions sender stores and
// targets this FID without touching the separate Expo mobile-token record.
const VAPID_PUBLIC_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";
const DEVICE_ID_PREFIX = "tallarge:web-chat-push-device:";

function getDeviceId(uid) {
  const key = `${DEVICE_ID_PREFIX}${uid}`;
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;

  const randomId = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : Array.from(window.crypto.getRandomValues(new Uint8Array(16)))
      .map((value) => value.toString(16).padStart(2, "0"))
      .join("");
  const deviceId = `web_${randomId}`;
  window.localStorage.setItem(key, deviceId);
  return deviceId;
}

function readDeviceId(uid) {
  try {
    return window.localStorage.getItem(`${DEVICE_ID_PREFIX}${uid}`) || "";
  } catch {
    return "";
  }
}

function normalizedChatPath(value) {
  const path = String(value || window.location.pathname || "/").trim();
  if (!path.startsWith("/") || path.startsWith("//")) return "/";
  return path.split(/[?#]/, 1)[0] || "/";
}

function getDeviceRef(uid, deviceId) {
  return doc(db, "drivers", uid, "webPushDevices", deviceId);
}

async function getWebPushDependencies() {
  if (typeof window === "undefined" || typeof Notification === "undefined") {
    throw new Error("此環境不支援網站通知。");
  }
  if (!window.isSecureContext) {
    throw new Error("網站通知需要 HTTPS 安全連線。");
  }
  if (!(await isSupported())) {
    throw new Error("此瀏覽器不支援 Tallarge 網站通知。");
  }
  if (!auth?.app) {
    throw new Error("Firebase 尚未初始化，無法設定網站通知。");
  }
  if (!VAPID_PUBLIC_KEY) {
    throw new Error("尚未設定 REACT_APP_FIREBASE_VAPID_KEY，請先填入 Firebase Cloud Messaging 的 Web Push 憑證。");
  }

  const appOptions = auth.app.options || {};
  if (!appOptions.apiKey || !appOptions.projectId || !appOptions.messagingSenderId || !appOptions.appId) {
    throw new Error("Firebase Web 設定缺少 Messaging 所需欄位。");
  }

  const scriptUrl = new URL("/firebase-messaging-sw.js", window.location.origin);
  scriptUrl.searchParams.set("firebaseConfig", JSON.stringify(appOptions));
  const serviceWorkerRegistration = await navigator.serviceWorker.register(
    `${scriptUrl.pathname}${scriptUrl.search}`,
    { scope: "/" },
  );
  const messaging = getMessaging(auth.app);
  return { messaging, serviceWorkerRegistration };
}

async function registerAndGetFid() {
  const { messaging, serviceWorkerRegistration } = await getWebPushDependencies();
  let unsubscribe = () => {};
  let timeout = null;

  const fidPromise = new Promise((resolve, reject) => {
    unsubscribe = onRegistered(messaging, (fid) => {
      const value = String(fid || "").trim();
      if (!value) return;
      if (timeout) window.clearTimeout(timeout);
      unsubscribe();
      resolve(value);
    });
    timeout = window.setTimeout(() => {
      unsubscribe();
      reject(new Error("Firebase 尚未回傳裝置 FID，請稍後重試。"));
    }, 15000);
  });

  try {
    await register(messaging, { vapidKey: VAPID_PUBLIC_KEY, serviceWorkerRegistration });
    const fid = await fidPromise;
    return { messaging, fid };
  } catch (error) {
    unsubscribe();
    if (timeout) window.clearTimeout(timeout);
    throw error;
  }
}

export async function enableWebChatPush(uid, chatPath) {
  const normalizedUid = String(uid || "").trim();
  if (!normalizedUid) throw new Error("尚未登入，無法開啟聊天室通知。");
  if (typeof window === "undefined" || typeof Notification === "undefined") {
    throw new Error("此環境不支援網站通知。");
  }
  if (!window.isSecureContext) throw new Error("網站通知需要 HTTPS 安全連線。");
  if (!VAPID_PUBLIC_KEY) {
    throw new Error("尚未設定 REACT_APP_FIREBASE_VAPID_KEY，請先填入 Firebase Cloud Messaging 的 Web Push 憑證。");
  }

  let permission = Notification.permission;
  if (permission === "default") permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(permission === "denied"
      ? "瀏覽器已封鎖通知，請到網站設定中允許通知後再試。"
      : "尚未允許通知，聊天室通知未開啟。");
  }

  const { fid } = await registerAndGetFid();
  const deviceId = getDeviceId(normalizedUid);
  const deviceRef = getDeviceRef(normalizedUid, deviceId);
  const previous = await getDoc(deviceRef);
  const oldData = previous.exists() ? previous.data() || {} : {};

  await setDoc(deviceRef, {
    uid: normalizedUid,
    deviceId,
    fid,
    platform: "web",
    enabled: true,
    chatPath: normalizedChatPath(chatPath),
    createdAt: oldData.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  return true;
}

export async function syncExistingWebChatPush(uid, chatPath) {
  const normalizedUid = String(uid || "").trim();
  if (!normalizedUid || typeof window === "undefined" || typeof Notification === "undefined") return false;
  if (Notification.permission !== "granted") return false;

  const deviceId = readDeviceId(normalizedUid);
  if (!deviceId) return false;
  const deviceRef = getDeviceRef(normalizedUid, deviceId);
  const snapshot = await getDoc(deviceRef);
  if (!snapshot.exists() || snapshot.data()?.enabled !== true) return false;

  const { fid } = await registerAndGetFid();
  await setDoc(deviceRef, {
    uid: normalizedUid,
    deviceId,
    fid,
    platform: "web",
    enabled: true,
    chatPath: normalizedChatPath(chatPath),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return true;
}

export async function disableWebChatPush(uid) {
  const normalizedUid = String(uid || "").trim();
  if (!normalizedUid) return false;

  const deviceId = readDeviceId(normalizedUid);
  if (deviceId) {
    await setDoc(getDeviceRef(normalizedUid, deviceId), {
      uid: normalizedUid,
      deviceId,
      enabled: false,
      fid: deleteField(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }

  try {
    if (auth?.app && typeof window !== "undefined" && (await isSupported())) {
      await unregister(getMessaging(auth.app));
    }
  } catch (error) {
    // The server-side enabled flag is the source of truth; local FID cleanup
    // can be retried without re-enabling the device.
    console.warn("[DriverManagerChatPush] FID unregister failed:", error);
  }

  try {
    window.localStorage.removeItem(`${DEVICE_ID_PREFIX}${normalizedUid}`);
  } catch (error) {
    console.warn("[DriverManagerChatPush] local device id cleanup failed:", error);
  }
  return false;
}

export async function subscribeToWebChatPushMessages(callback, uid) {
  if (typeof window === "undefined" || typeof Notification === "undefined") return () => {};
  if (Notification.permission !== "granted" || !(await isSupported())) return () => {};
  if (!auth?.app) return () => {};
  const messaging = getMessaging(auth.app);
  const unsubscribeMessage = onMessage(messaging, (payload) => {
    const data = payload?.data || {};
    if (data.type !== "chat_message") return;
    callback({
      orgId: String(data.orgId || ""),
      chatId: String(data.chatId || ""),
      messageId: String(data.messageId || ""),
      senderUid: String(data.senderUid || ""),
      senderName: String(data.senderName || "Tallarge 成員"),
      chatTitle: String(data.chatTitle || "聊天室"),
      title: String(data.title || "聊天室新訊息"),
      body: String(data.body || "你收到一則新的聊天室訊息。"),
    });
  });
  const normalizedUid = String(uid || "").trim();
  const unsubscribeRegistered = normalizedUid ? onRegistered(messaging, (fid) => {
    const deviceId = readDeviceId(normalizedUid);
    const value = String(fid || "").trim();
    if (!deviceId || !value) return;
    const deviceRef = getDeviceRef(normalizedUid, deviceId);
    getDoc(deviceRef).then((snapshot) => {
      if (!snapshot.exists() || snapshot.data()?.enabled !== true) return;
      return setDoc(deviceRef, { fid: value, updatedAt: serverTimestamp() }, { merge: true });
    }).catch((error) => {
      console.warn("[DriverManagerChatPush] FID refresh sync failed:", error);
    });
  }) : () => {};

  return () => {
    unsubscribeMessage();
    unsubscribeRegistered();
  };
}
